import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { CustomerSelect } from "@/components/transaction/CustomerSelect";
import { Plus, X } from "lucide-react";
import { NumericFormat } from "react-number-format";
import { useQueryClient } from "@tanstack/react-query";

interface QuotationFormProps {
  onClose: () => void;
  initialData?: any;
}

interface Customer {
  id: string;
  name: string;
  phone: string;
  whatsapp?: string;
  description?: string;
}

interface Material {
  name: string;
  quantity: number;
  price: number;
}

export function QuotationForm({ onClose, initialData }: QuotationFormProps) {
  const [description, setDescription] = useState("");
  const [totalAmount, setTotalAmount] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("");
  const [validityDate, setValidityDate] = useState("");
  const [executionDeadline, setExecutionDeadline] = useState("");
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [customerName, setCustomerName] = useState("");
  const [materials, setMaterials] = useState<Material[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [lastQuotationHash, setLastQuotationHash] = useState<string | null>(null);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  useEffect(() => {
    if (initialData) {
      setDescription(initialData.description);
      setTotalAmount(initialData.total_amount.toString());
      setPaymentMethod(initialData.payment_method);
      setValidityDate(initialData.validity_date);
      setExecutionDeadline(initialData.execution_deadline);
      setMaterials(initialData.materials || []);
      if (initialData.customer) {
        setSelectedCustomer(initialData.customer);
        setCustomerName(initialData.customer.name);
      }
    }
  }, [initialData]);

  const createQuotationHash = (data: any): string => {
    const hashData = {
      customer_id: data.customer_id,
      description: data.description || "",
      total_amount: data.total_amount,
      payment_method: data.payment_method || "",
      materials: data.materials || []
    };
    return JSON.stringify(hashData);
  };

  const clearQuotationsCache = () => {
    if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
      navigator.serviceWorker.controller.postMessage({
        type: 'CLEAR_QUOTATIONS_CACHE'
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (isSubmitting) {
      toast({
        title: "Processando",
        description: "Aguarde, sua solicitação está sendo processada.",
      });
      return;
    }
    
    if (!selectedCustomer) {
      toast({
        title: "Erro",
        description: "Por favor, selecione um cliente.",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsSubmitting(true);
      const numericTotalAmount = parseFloat(totalAmount.replace(/[^\d,-]/g, '').replace(',', '.'));
      
      const quotationData = {
        description,
        total_amount: numericTotalAmount,
        payment_method: paymentMethod,
        validity_date: validityDate,
        execution_deadline: executionDeadline,
        materials,
        customer_id: selectedCustomer.id,
      };

      const currentHash = createQuotationHash(quotationData);
      
      if (lastQuotationHash && lastQuotationHash === currentHash) {
        const timeSinceLastSubmit = Date.now() - (parseInt(lastQuotationHash.split('timestamp:')[1] || '0') || 0);
        if (timeSinceLastSubmit < 10000) {
          console.warn('Duplicate quotation detected via hash match');
          toast({
            title: "Atenção",
            description: "Orçamento idêntico enviado recentemente. Evitando duplicação.",
            variant: "destructive",
          });
          setIsSubmitting(false);
          return;
        }
      }
      
      if (!initialData) {
        const oneMinuteAgo = new Date();
        oneMinuteAgo.setMinutes(oneMinuteAgo.getMinutes() - 1);
        
        const { data: existingQuotations, error: checkError } = await supabase
          .from('quotations')
          .select('id, created_at, total_amount')
          .eq('customer_id', selectedCustomer.id)
          .gte('created_at', oneMinuteAgo.toISOString())
          .order('created_at', { ascending: false });
          
        if (checkError) {
          console.error("Erro ao verificar orçamentos existentes:", checkError);
        } else if (existingQuotations && existingQuotations.length > 0) {
          const similarQuotation = existingQuotations.find(q => {
            const difference = Math.abs(q.total_amount - numericTotalAmount);
            const percentDifference = difference / numericTotalAmount * 100;
            return percentDifference < 5;
          });
          
          if (similarQuotation) {
            console.warn('Potential duplicate quotation found in database');
            const confirmContinue = window.confirm(
              "Um orçamento similar para este cliente foi criado recentemente. Deseja continuar mesmo assim?"
            );
            
            if (!confirmContinue) {
              setIsSubmitting(false);
              return;
            }
          }
        }
      }

      if (initialData) {
        const { error } = await supabase
          .from("quotations")
          .update(quotationData)
          .eq("id", initialData.id);

        if (error) throw error;

        toast({
          title: "Orçamento atualizado com sucesso!",
          description: "As alterações foram salvas no sistema.",
        });
      } else {
        const { error, data } = await supabase
          .from("quotations")
          .insert(quotationData)
          .select();

        if (error) throw error;
        
        if (data && data.length > 0) {
          setLastQuotationHash(currentHash + `timestamp:${Date.now()}`);
        }

        toast({
          title: "Orçamento criado com sucesso!",
          description: "O orçamento foi salvo no sistema.",
        });
      }
      
      clearQuotationsCache();
      
      queryClient.invalidateQueries({ queryKey: ["quotations"] });
      
      onClose();
    } catch (error) {
      console.error("Error saving quotation:", error);
      toast({
        title: "Erro ao salvar orçamento",
        description: "Ocorreu um erro ao salvar o orçamento.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const addMaterial = () => {
    setMaterials([...materials, { name: "", quantity: 0, price: 0 }]);
  };

  const removeMaterial = (index: number) => {
    setMaterials(materials.filter((_, i) => i !== index));
  };

  const updateMaterial = (index: number, field: keyof Material, value: string | number) => {
    const updatedMaterials = [...materials];
    updatedMaterials[index] = {
      ...updatedMaterials[index],
      [field]: typeof value === 'string' ? parseFloat(value.replace(/[^\d,-]/g, '').replace(',', '.')) || 0 : value
    };
    setMaterials(updatedMaterials);

    const newTotal = updatedMaterials.reduce((sum, material) => 
      sum + (material.quantity * material.price), 0
    );
    setTotalAmount(newTotal.toString());
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <CustomerSelect
        selectedCustomer={selectedCustomer}
        setSelectedCustomer={setSelectedCustomer}
        customerName={customerName}
        setCustomerName={setCustomerName}
      />
      
      <div>
        <label className="block text-sm font-medium mb-1">Descrição</label>
        <Textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          required
          className="bg-[#1A1F2C] text-white border-[#404040]"
        />
      </div>

      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <label className="block text-sm font-medium">Materiais</label>
          <Button 
            type="button" 
            onClick={addMaterial}
            variant="outline"
            size="sm"
            className="bg-[#0073B7] hover:bg-[#004080] text-white border-none"
          >
            <Plus className="h-4 w-4 mr-1" />
            Adicionar Material
          </Button>
        </div>

        {materials.map((material, index) => (
          <div key={index} className="flex gap-2 items-start">
            <div className="flex-1">
              <Input
                placeholder="Nome do material"
                value={material.name}
                onChange={(e) => {
                  const updatedMaterials = [...materials];
                  updatedMaterials[index] = {
                    ...updatedMaterials[index],
                    name: e.target.value
                  };
                  setMaterials(updatedMaterials);
                }}
                className="bg-[#1A1F2C] text-white border-[#404040]"
              />
            </div>
            <div className="w-24">
              <NumericFormat
                customInput={Input}
                thousandSeparator="."
                decimalSeparator=","
                decimalScale={0}
                placeholder="Qtd"
                value={material.quantity}
                onValueChange={(values) => {
                  const updatedMaterials = [...materials];
                  updatedMaterials[index] = {
                    ...updatedMaterials[index],
                    quantity: values.floatValue || 0
                  };
                  setMaterials(updatedMaterials);
                }}
                className="bg-[#1A1F2C] text-white border-[#404040]"
              />
            </div>
            <div className="w-32">
              <NumericFormat
                customInput={Input}
                thousandSeparator="."
                decimalSeparator=","
                decimalScale={2}
                prefix="R$ "
                placeholder="Preço"
                value={material.price}
                onValueChange={(values) => {
                  const updatedMaterials = [...materials];
                  updatedMaterials[index] = {
                    ...updatedMaterials[index],
                    price: values.floatValue || 0
                  };
                  setMaterials(updatedMaterials);
                  
                  const newTotal = updatedMaterials.reduce((sum, mat) => 
                    sum + (mat.quantity * mat.price), 0
                  );
                  setTotalAmount(newTotal.toString());
                }}
                className="bg-[#1A1F2C] text-white border-[#404040]"
              />
            </div>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={() => removeMaterial(index)}
              className="hover:bg-red-500/10 hover:text-red-500"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        ))}
      </div>

      <div>
        <label className="block text-sm font-medium mb-1">Valor Total</label>
        <NumericFormat
          customInput={Input}
          thousandSeparator="."
          decimalSeparator=","
          decimalScale={2}
          prefix="R$ "
          value={totalAmount}
          onValueChange={(values) => {
            setTotalAmount(values.value);
          }}
          required
          className="bg-[#1A1F2C] text-white border-[#404040]"
        />
      </div>
      
      <div>
        <label className="block text-sm font-medium mb-1">Método de Pagamento</label>
        <Input
          value={paymentMethod}
          onChange={(e) => setPaymentMethod(e.target.value)}
          required
          className="bg-[#1A1F2C] text-white border-[#404040]"
        />
      </div>
      
      <div>
        <label className="block text-sm font-medium mb-1">Data de Validade</label>
        <Input
          type="date"
          value={validityDate}
          onChange={(e) => setValidityDate(e.target.value)}
          required
          className="bg-[#1A1F2C] text-white border-[#404040]"
        />
      </div>
      
      <div>
        <label className="block text-sm font-medium mb-1">Prazo de Execução</label>
        <Input
          value={executionDeadline}
          onChange={(e) => setExecutionDeadline(e.target.value)}
          required
          className="bg-[#1A1F2C] text-white border-[#404040]"
        />
      </div>
      
      <div className="flex justify-end space-x-2">
        <Button 
          variant="outline" 
          type="button" 
          onClick={onClose}
          className="border-[#404040] text-white hover:bg-[#1A1F2C]"
        >
          Cancelar
        </Button>
        <Button 
          type="submit"
          className="bg-[#0073B7] hover:bg-[#004080] text-white"
          disabled={isSubmitting}
        >
          {isSubmitting ? (
            <>
              <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2"></div>
              {initialData ? "Atualizando..." : "Salvando..."}
            </>
          ) : (
            initialData ? "Atualizar" : "Salvar"
          )}
        </Button>
      </div>
    </form>
  );
}
